import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../../context/auth/auth-context'
import axios from 'axios'
import { type AlertColor, Box, Chip, IconButton, Modal, Stack, Typography, useTheme } from '@mui/material'
import { DataGridPro, useGridApiRef, type GridRowId, type GridColDef, GridActionsCellItem } from '@mui/x-data-grid-pro'
import CloseIcon from '@mui/icons-material/Close'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../../utils/time-utils'
import { formAlertErrorMessage, formAlertResponseMessageWithPrompt } from '../../../../utils/string-utils'
import { LIVE_ORDERS_AUTOSIZE_OPTIONS, LIVE_ORDERS_COL_DEF, LIVE_ORDERS_SORT_MODEL } from './live-orders-modal-col-definitions'
import { type LiveOrdersDataGridRow, prepareLiveOrdersForDataGrid } from '../../../../utils/view-specific/strategy/strategy-utils'
import ReloadFilterExportGridToolbar, { type FilterExportGridToolbarProps, } from '../../../../components/data-grid/reload-filter-export-grid-toolbar'
import ConfirmationModal from '../../../../components/modal/confirmation-modal'
import ToastWithMessage from '../../../../components/toast/toast-with-message'

// Augment the props for the toolbar slot
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends FilterExportGridToolbarProps {
    // Intentionally left empty
  }
}

interface LiveOrdersModalProps {
  open: boolean
  onClose: () => void
  strategyId: string
  title: string
  dataUrl: string | null
  postUrl: string | null
}

const LiveOrdersModal: React.FC<LiveOrdersModalProps> = (
  { open, onClose, strategyId, title, dataUrl, postUrl }
) => {
  const theme = useTheme()
  const [data, setData] = useState<LiveOrdersDataGridRow[]>([])
  const [retrievalTimestamp, setRetrievalTimestamp] = useState<number>(0)

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const getLiveOrders = (): void => {
    if (dataUrl != null && dataUrl.length !== 0) {
      axios
        .get(dataUrl, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then(response => {
          setData(prepareLiveOrdersForDataGrid(response.data))
          setRetrievalTimestamp(Date.now())
        })
        .catch(reason => {
          console.error(reason)
          setData([])
          setRetrievalTimestamp(-1)
        })
    }
  }

  useEffect(() => {
    if (open && isAuthorized) {
      getLiveOrders()
    }
  }, [open, isAuthorized])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = (message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }

  const [selectedRowId, setSelectedRowId] = useState<GridRowId | null>(null)
  const handleCloseOrder = useCallback((): void => {
    if (postUrl == null || postUrl.length === 0) {
      console.debug('[Live Orders Modal] Post url is empty')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    if (selectedRowId == null) {
      console.debug('[Live Orders Modal] Selected row id is null')
      displayToastWithMessage('The selected row id is empty - please reload to get the latest live orders', 'warning')
      return
    }

    const rowData = data.find(item => item.orderId === selectedRowId)
    if (rowData == null) {
      console.debug('[Live Orders Modal] Selected row data is null')
      displayToastWithMessage('The selected row has no data - please reload to get the latest live orders', 'warning')
      return
    }

    const message = {
      timestamp: new Date().toISOString(),
      orderId: rowData.orderId,
      symbol: rowData.symbol,
      reason: 'Manually force close order',
    }

    console.debug(message)
    console.debug(`[Live Orders Modal] Force closing order: ${rowData.orderId}`)
    axios
      .post(postUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        console.debug(response)
        displayToastWithMessage(formAlertResponseMessageWithPrompt(response), 'success')
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
  }, [data, selectedRowId, accessToken])

  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const openModal = (): void => { setModalOpen(true) }
  const closeModal = (): void => {
    setModalOpen(false)
    setSelectedRowId(null)
  }

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      sortable: false,
      getActions: (params: { id: React.SetStateAction<GridRowId | null> }) => [
        <GridActionsCellItem
          key='close-order-action'
          icon={<HighlightOffIcon color={'error'}/>}
          label='Force Close Order'
          onClick={() => {
            setSelectedRowId(params.id)
            openModal()
          }}
        />,
      ],
    },
    ...LIVE_ORDERS_COL_DEF,
  ]

  const apiRef = useGridApiRef()
  useEffect(() => {
    if (apiRef.current != null && typeof apiRef.current.autosizeColumns === 'function') {
      void (async function () {
        await apiRef.current.autosizeColumns(LIVE_ORDERS_AUTOSIZE_OPTIONS)
      })()
    }
  }, [apiRef, data])

  const onModalClose = useCallback(() => {
    setToastOpen(false)
    onClose()
  }, [onClose])

  return (
    <Modal
      open={open}
      onClose={onModalClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1600px',
          height: '80%',
          maxHeight: '1000px',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: 24,
          p: 4,
          color: 'white'
        }}
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography id='modal-modal-title' fontWeight='bold' variant='h5' component='h1'>
            {title}
          </Typography>
          <Chip
            label={formatToLocalMilliTimestampFromUTCNumber(retrievalTimestamp)}
            size='small'
          />
          <Box marginX='auto'></Box>
          <IconButton color='error' onClick={onModalClose}>
            <CloseIcon/>
          </IconButton>
        </Stack>

        <Box
          sx={{
            height: 'calc(100% - 80px)'
          }}
        >
          <DataGridPro
            apiRef={apiRef}
            getRowId={row => row.orderId}
            rowHeight={20}
            autosizeOnMount={true}
            autosizeOptions={LIVE_ORDERS_AUTOSIZE_OPTIONS}
            sortModel={LIVE_ORDERS_SORT_MODEL}
            slots={{
              toolbar: ReloadFilterExportGridToolbar,
            }}
            slotProps={{
              toolbar: {
                handleReload: getLiveOrders,
                csvOptions: { fileName: title }
              }
            }}
            disableMultipleRowSelection={true}
            columns={columns}
            rows={data}
            loading={retrievalTimestamp <= 0}
          />
        </Box>

        <ConfirmationModal
          open={modalOpen}
          onClose={closeModal}
          onHandle={handleCloseOrder}
          title={'Force close order?'}
          description={'This will manually force close the order and it will be removed from the live order cache.'}
          handleButtonLabel={'Force Close Order'}
          handleButtonColour={'primary'}
        />

        <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
      </Box>
    </Modal>
  )
}

export default LiveOrdersModal
